import type { FormEvent } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";

import { useSession } from "../auth/useSession";
import { isLegacyAuthMode } from "../lib/env";

type LocationState = { from?: string } | null;

const LEGACY_AUTH = isLegacyAuthMode();

export default function LoginPage() {
  const location = useLocation();
  const state = location.state as LocationState;
  const redirectTo = state?.from && state.from !== "/login" ? state.from : "/";

  const { user, isLoading, isError, login, loginPending, loginError } = useSession();

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-slate-500">
        Session wird geprüft …
      </div>
    );
  }

  if (isError) {
    return <Navigate to={redirectTo} replace />;
  }

  if (user) {
    return <Navigate to={redirectTo} replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const email = String(formData.get("email") ?? "").trim();
    const password = String(formData.get("password") ?? "").trim();
    if (!email) return;
    try {
      await login({ email, password });
    } catch {
      // surfaced via loginError below
    }
  }

  const errorMessage = loginError ? loginError.message : null;

  return (
    <main className="mx-auto flex min-h-full max-w-sm flex-col justify-center px-6 py-10">
      <h1 className="text-2xl font-semibold text-slate-900">Anmelden</h1>
      <p className="mt-1 text-sm text-slate-500">
        TruuCost Beta
      </p>
      <form onSubmit={handleSubmit} className="mt-6 space-y-4">
        <label className="block text-sm">
          <span className="text-slate-700">
            {LEGACY_AUTH ? "Benutzername" : "E-Mail"}
          </span>
          <input
            type={LEGACY_AUTH ? "text" : "email"}
            name="email"
            autoComplete={LEGACY_AUTH ? "username" : "email"}
            autoFocus
            required
            defaultValue=""
            className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none"
          />
        </label>
        {!LEGACY_AUTH && (
          <label className="block text-sm">
            <span className="text-slate-700">Passwort</span>
            <input
              type="password"
              name="password"
              autoComplete="current-password"
              required
              className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none"
            />
          </label>
        )}
        {errorMessage && (
          <p className="text-sm text-red-600">{errorMessage}</p>
        )}
        <button
          type="submit"
          disabled={loginPending}
          className="w-full rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-slate-800 disabled:opacity-50"
        >
          {loginPending ? "Melde an …" : "Anmelden"}
        </button>
      </form>
      {LEGACY_AUTH && (
        <p className="mt-4 text-xs text-slate-400">
          Dev-Modus: beliebiger Benutzername, kein Passwort erforderlich.
        </p>
      )}
      <p className="mt-8 text-center text-xs text-slate-400">
        <Link to="/impressum" className="hover:text-slate-600">
          Impressum
        </Link>
        <span className="px-2">·</span>
        <Link to="/datenschutz" className="hover:text-slate-600">
          Datenschutz
        </Link>
      </p>
    </main>
  );
}
